import { useViewport } from 'contexts/viewport';
import moment from 'moment';
import { PageContext } from 'pages/Grading/PageProvider';
import { useContext } from 'react';
import { Button, Divider, Dropdown, Icon, IconButton, Stack, Text, Tooltip, styled } from 'shell/components';

const Header = () => {
    const { device } = useViewport();

    const {
        submissions,
        currentSubmission,
        isCurrentFormValid,
        isGradesGenerating,
        isFeedbackGenerating,
        handleClickConfirm,
        handleClickNext,
        handleSelectSubmission,
        handleClickClose,
        handleClickEdit,
    } = useContext(PageContext);

    const isGenerating = isGradesGenerating || isFeedbackGenerating;

    const CompletionIconComponent = <CompletionIcon kind={'checkmark'} isCompleted={currentSubmission.locked} />;

    const SubmissionTitle = (
        <Text component={'h1'} noWrap title={currentSubmission.data.name} variant={'p1'}>
            {currentSubmission.data.original_file
                ? currentSubmission.data.original_file.name
                : currentSubmission.data.name}
        </Text>
    );

    const SubmissionTimestamp = (
        <Text variant={'p3'}>Uploaded: {moment(currentSubmission.created_on).format('MMM D, YYYY')}</Text>
    );

    const CloseIconButton = (
        <IconButton
            aria-label={'close'}
            disabled={isGenerating}
            kind={'exit'}
            size={device === 'mobile' ? 'small' : 'medium'}
            variant={'square'}
            onClick={handleClickClose}
        />
    );

    const NavigationButtons = (
        <>
            {currentSubmission.locked ? (
                <Button
                    disabled={isGenerating}
                    startIcon={<Icon kind={'edit'} />}
                    tooltip={{ component: 'Edit grades & feedback', maxWidth: '200px' }}
                    onClick={handleClickEdit}
                >
                    Edit
                </Button>
            ) : (
                <Button
                    className={'joyride-step-11'}
                    disabled={isGenerating || !isCurrentFormValid}
                    tooltip={{ component: 'Confirm grades & feedback', maxWidth: '200px' }}
                    onClick={handleClickConfirm}
                >
                    Confirm
                </Button>
            )}
            {submissions.every(({ locked }) => locked) ? (
                <Button disabled={isGenerating} variant={'secondary'} onClick={handleClickClose}>
                    Finish{device === 'mobile' ? '' : ' Grading'}
                </Button>
            ) : (
                <Button
                    disabled={isGenerating || submissions.length === 1}
                    variant={'secondary'}
                    onClick={handleClickNext}
                >
                    Next
                </Button>
            )}
        </>
    );

    const NavigationDropdown = (
        <Stack>
            <Text weight={'semibold'}>Progress</Text>
            <Dropdown
                id={'grading-select-submission-dropdown'}
                options={submissions.map(({ id, data, locked }, index) => {
                    const submissionName = data.original_file ? data.original_file.name : data.name;
                    return {
                        key: id,
                        value: (
                            <Stack
                                sx={{ width: '256px' }}
                                alignItems={'center'}
                                direction={'row'}
                                justifyContent={'space-between'}
                                spacing={4}
                            >
                                <Text title={submissionName} noWrap>{`${index + 1}. ${submissionName}`}</Text>
                                <Tooltip title={locked ? 'Reviewed' : 'Ready for review'}>
                                    <Icon kind={locked ? 'checkmark' : 'clock'} />
                                </Tooltip>
                            </Stack>
                        ),
                    };
                })}
                rootComponent={
                    <Button
                        color={'neutral'}
                        disabled={isGenerating}
                        endIcon={<Icon kind={'dropdownArrowDown'} />}
                        variant={'borderless'}
                    >
                        {submissions.findIndex(({ id }) => id === currentSubmission.id) + 1}/{submissions.length}
                    </Button>
                }
                onSelect={({ key: submissionId }) => handleSelectSubmission(submissionId)}
            />
        </Stack>
    );

    return device === 'mobile' ? (
        <RootStack device={device} justifyContent={'space-between'} spacing={2}>
            <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                <Stack sx={{ minWidth: 0 }} alignItems={'flex-start'} direction={'row'} spacing={2}>
                    {CompletionIconComponent}
                    <Stack sx={{ minWidth: 0 }} spacing={1}>
                        {SubmissionTitle}
                        {SubmissionTimestamp}
                    </Stack>
                </Stack>
                {CloseIconButton}
            </Stack>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                {NavigationDropdown}
                <Stack direction={'row'} spacing={2}>
                    {NavigationButtons}
                </Stack>
            </Stack>
        </RootStack>
    ) : (
        <RootStack
            alignItems={'center'}
            device={device}
            direction={'row'}
            divider={<Divider flexItem orientation={'vertical'} />}
            justifyContent={'space-between'}
            spacing={4}
        >
            <Stack sx={{ flex: 1, minWidth: 0 }} direction={'row'} spacing={2}>
                {CompletionIconComponent}
                <Stack sx={{ minWidth: 0 }} spacing={1}>
                    {SubmissionTitle}
                    {SubmissionTimestamp}
                </Stack>
            </Stack>
            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                {NavigationButtons}
                {NavigationDropdown}
            </Stack>
            {CloseIconButton}
        </RootStack>
    );
};

const RootStack = styled(Stack)(({ theme, device }) => {
    return {
        position: 'absolute',
        top: 0,
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        width: '100%',
        height: device === 'mobile' ? '129px' : '73px',
        padding: device === 'mobile' ? theme.spacing(3, 4) : theme.spacing(3, 5),
        background: theme.palette.ensightful.primary[50],
    };
});

const CompletionIcon = styled(Icon)(({ theme, isCompleted }) => {
    return {
        borderRadius: '50%',
        width: '16px',
        height: '16px',
        padding: theme.spacing(0.75),
        ...(isCompleted
            ? {
                  background: theme.palette.ensightful.success[700],
                  color: theme.palette.ensightful.shades[0],
              }
            : {
                  border: `1px solid ${theme.palette.ensightful.neutral[700]}`,
                  color: theme.palette.ensightful.neutral[700],
              }),
    };
});

export default Header;
